/**
 * Gartner Demo mock data — all mock constants for the GartnerDemo feature flag.
 * Delete this file and all imports to remove the demo.
 */
import { DescriptorType, FieldListObject } from '@fabric-ui/components';
import { FieldType, FieldTypeEnum } from '../Sources/Mapping/types';
import { ScoreExplanationData } from '../Tickets/ScoreExplanationTab/types';
import { EntityBuildingBlock, EntityWeight, ProjectionToBaseScore } from './types';

// DescriptorType numeric values used as fieldMap .type — these are the keys typeLabelMap resolves.
// Must be numeric DescriptorType (not FieldType strings) to match real fieldMap entries.
// Cast needed because FieldTypeEnum reverse-maps return string | DescriptorType.
const BOOL_TYPE = FieldTypeEnum[FieldType.Boolean] as DescriptorType; // TYPE_BOOL = 8
const NUM_TYPE = FieldTypeEnum[FieldType.Number] as DescriptorType; // TYPE_DOUBLE = 1

// ---------------------------------------------------------------------------
// Field stubs — injected into fieldMap and aggProjs[proj].fieldList so that
// ScoringEditFactor can resolve .type/.title and populate the field dropdown.
// Typed as FieldListObject (with required fields as any) so the merged fieldMap
// stays compatible with Record<string, FieldListObject>.
// ---------------------------------------------------------------------------

const mkFieldStub = (
  fieldName: string,
  type: DescriptorType,
  title: string,
  displayName: string,
  group: string,
  projection: string
): FieldListObject =>
  ({
    id: fieldName,
    value: fieldName,
    name: fieldName,
    systemName: fieldName,
    title,
    displayName,
    group,
    type,
    builtIn: true,
    pathAlias: projection,
    mainProjId: { name: projection, builtIn: true },
    entityTypeId: { name: projection, builtIn: true },
    repeated: false,
    tags: [],
    originalType: {},
    visibilityConfig: {
      projectionName: projection,
      builtInProjection: true,
      fieldName,
      config: { hidden: false },
    },
  }) as unknown as FieldListObject;

// Each key is the fieldName; value is a full FieldListObject stub.
// mkFieldStub fills all required FieldListObject fields so the merged fieldMap
// stays typed as Record<string, FieldListObject> without TS errors.
export const GARTNER_MOCK_FIELD_STUBS: Record<string, FieldListObject> = {
  'finding.vulnerability_in_wild': mkFieldStub(
    'finding.vulnerability_in_wild',
    BOOL_TYPE,
    'Vulnerability seen in the wild',
    'Vulnerability seen in the wild',
    'Finding',
    'uber_findings'
  ),
  'finding.exploit_on_decoy': mkFieldStub(
    'finding.exploit_on_decoy',
    BOOL_TYPE,
    'Exploit seen on a decoy',
    'Exploit seen on a decoy',
    'Finding',
    'uber_findings'
  ),
  'finding.involved_in_ransomware': mkFieldStub(
    'finding.involved_in_ransomware',
    BOOL_TYPE,
    'Involved in ransomware',
    'Involved in ransomware',
    'Finding',
    'uber_findings'
  ),
  'finding.active_exploitation': mkFieldStub(
    'finding.active_exploitation',
    BOOL_TYPE,
    'Active exploitation',
    'Active exploitation',
    'Finding',
    'uber_findings'
  ),
  'finding.exploit_maturity_score': mkFieldStub(
    'finding.exploit_maturity_score',
    NUM_TYPE,
    'Exploit Maturity Score',
    'Exploit Maturity Score',
    'Finding',
    'uber_findings'
  ),
  'finding.mitigated_by_zpa': mkFieldStub(
    'finding.mitigated_by_zpa',
    BOOL_TYPE,
    'Mitigated by ZPA',
    'Mitigated by ZPA',
    'Finding',
    'uber_findings'
  ),
  'finding.mitigated_by_zia': mkFieldStub(
    'finding.mitigated_by_zia',
    BOOL_TYPE,
    'Mitigated by ZIA',
    'Mitigated by ZIA',
    'Finding',
    'uber_findings'
  ),
  'finding.original_severity_score': mkFieldStub(
    'finding.original_severity_score',
    NUM_TYPE,
    'Finding Original Severity Score',
    'Original Severity Score',
    'Finding',
    'uber_findings'
  ),
  'asset.business_criticality': mkFieldStub(
    'asset.business_criticality',
    NUM_TYPE,
    'Business Criticality',
    'Business Criticality',
    'Asset',
    'uber_assets'
  ),
  'asset.has_pii': mkFieldStub('asset.has_pii', BOOL_TYPE, 'Asset has PII', 'Asset has PII', 'Asset', 'uber_assets'),
};

// Grouped by projection — used only by buildGartnerAccountEntities below.
const GARTNER_MOCK_FIELD_LIST_ENTRIES: Record<string, FieldListObject[]> = Object.entries(GARTNER_MOCK_FIELD_STUBS).reduce(
  (acc, [, stub]) => {
    const proj = stub.pathAlias;
    if (!acc[proj]) acc[proj] = [];
    acc[proj].push(stub);
    return acc;
  },
  {} as Record<string, FieldListObject[]>
);

/**
 * Merges Gartner mock field stubs into a real accountEntities object.
 * Pass the result as `accountEntitiesOverride` to ScoringEditFactor.
 *   - fieldMap: mock stubs win over real entries (correct type/title for mock fields)
 *   - aggProjs[proj].fieldList.*: mock fields prepended so they appear in the field dropdown
 */
export const buildGartnerAccountEntities = <T extends { aggProjs: Record<string, any>; fieldMap: Record<string, FieldListObject> }>(
  baseEntities: T
): T => {
  const mergedAggProjs = Object.fromEntries(
    Object.entries(baseEntities.aggProjs).map(([projName, proj]) => {
      const mockEntries = GARTNER_MOCK_FIELD_LIST_ENTRIES[projName];
      if (!mockEntries) return [projName, proj];
      const mergedFieldList = { ...proj.fieldList };
      Object.keys(mergedFieldList).forEach(key => {
        mergedFieldList[key] = [...mockEntries, ...mergedFieldList[key]];
      });
      return [projName, { ...proj, fieldList: mergedFieldList }];
    })
  ) as T['aggProjs'];
  return {
    ...baseEntities,
    fieldMap: { ...baseEntities.fieldMap, ...GARTNER_MOCK_FIELD_STUBS } as T['fieldMap'],
    aggProjs: mergedAggProjs,
  };
};

// ---------------------------------------------------------------------------
// Base scores (factors per projection)
// ---------------------------------------------------------------------------

const mkBoolFactor = (fieldName: string, displayName: string, trueScore: number, falseScore: number) => ({
  fieldName,
  displayName,
  fallback: falseScore,
  scoringRules: [
    { value: trueScore, filter: { expression: { fieldName, boolCondition: { equals: true } } } },
    { value: falseScore, filter: { expression: { fieldName, boolCondition: { equals: false } } } },
  ],
});

const mkNumberFactor = (fieldName: string, displayName: string, score: number) => ({
  fieldName,
  displayName,
  fallback: 0,
  scoringRules: [{ value: score, filter: null, adjustmentType: 'FIELD_BASED' as const }],
});

export const GARTNER_MOCK_BASE_SCORES: ProjectionToBaseScore[] = [
  {
    projectionName: 'uber_findings',
    baseScore: {
      id: null,
      accountId: '',
      projectionName: 'uber_findings',
      config: {
        configTypes: [
          {
            type: 'CVE',
            isDefault: true,
            factorGroups: [
              {
                name: 'Exploitability',
                weight: 0.6,
                factors: [
                  mkBoolFactor('finding.vulnerability_in_wild', 'Vulnerability seen in the wild', 15, 0),
                  mkBoolFactor('finding.exploit_on_decoy', 'Exploit seen on a decoy', 20, 0),
                  mkBoolFactor('finding.involved_in_ransomware', 'Involved in ransomware', 10, 0),
                  mkBoolFactor('finding.active_exploitation', 'Active exploitation', 20, 0),
                  mkNumberFactor('finding.exploit_maturity_score', 'Exploit Maturity Score', 0),
                ],
              },
              {
                name: 'Exposure',
                weight: 0.4,
                factors: [
                  mkBoolFactor('finding.mitigated_by_zpa', 'Mitigated by ZPA', -10, 0),
                  mkBoolFactor('finding.mitigated_by_zia', 'Mitigated by ZIA', -10, 0),
                ],
              },
            ],
          },
          {
            type: 'Misconfig',
            isDefault: false,
            factorGroups: [
              {
                name: 'DEFAULT',
                weight: 1,
                factors: [
                  {
                    fieldName: 'finding.original_severity_score',
                    displayName: 'Original Severity Score',
                    fallback: 0,
                    scoringRules: [{ value: 1, filter: null, adjustmentType: 'FIELD_BASED' as const }],
                  },
                ],
              },
            ],
          },
        ],
      },
    },
  },
  {
    projectionName: 'uber_assets',
    baseScore: {
      id: null,
      accountId: '',
      projectionName: 'uber_assets',
      config: {
        configTypes: [
          {
            type: 'DEFAULT',
            isDefault: true,
            factorGroups: [
              {
                name: 'DEFAULT',
                weight: 1,
                factors: [
                  {
                    fieldName: 'asset.business_criticality',
                    displayName: 'Business Criticality',
                    fallback: 0,
                    scoringRules: [
                      { value: 0, filter: { expression: { fieldName: 'asset.business_criticality', numberCondition: { equals: 1 } } } },
                      { value: 5, filter: { expression: { fieldName: 'asset.business_criticality', numberCondition: { equals: 2 } } } },
                      { value: 10, filter: { expression: { fieldName: 'asset.business_criticality', numberCondition: { equals: 3 } } } },
                      { value: 20, filter: { expression: { fieldName: 'asset.business_criticality', numberCondition: { equals: 4 } } } },
                      { value: 30, filter: { expression: { fieldName: 'asset.business_criticality', numberCondition: { equals: 5 } } } },
                    ],
                  },
                  mkBoolFactor('asset.has_pii', 'Asset has PII', 15, 0),
                ],
              },
            ],
          },
        ],
      },
    },
  },
];

// ---------------------------------------------------------------------------
// Building blocks (left-panel factor list)
// ---------------------------------------------------------------------------

export const GARTNER_MOCK_BUILDING_BLOCKS: EntityBuildingBlock[] = [
  {
    projectionId: 'uber_findings',
    name: 'Finding',
    activeCount: 7,
    activeLabel: 'Factors',
    factors: [
      { name: 'finding.vulnerability_in_wild', displayName: 'Vulnerability seen in the wild' },
      { name: 'finding.exploit_on_decoy', displayName: 'Exploit seen on a decoy' },
      { name: 'finding.involved_in_ransomware', displayName: 'Involved in ransomware' },
      { name: 'finding.active_exploitation', displayName: 'Active exploitation' },
      { name: 'finding.exploit_maturity_score', displayName: 'Exploit Maturity Score' },
      { name: 'finding.mitigated_by_zpa', displayName: 'Mitigated by ZPA' },
      { name: 'finding.mitigated_by_zia', displayName: 'Mitigated by ZIA' },
    ],
    configTypes: GARTNER_MOCK_BASE_SCORES[0].baseScore.config.configTypes,
  },
  {
    projectionId: 'uber_assets',
    name: 'Asset Business Context',
    activeCount: 2,
    activeLabel: 'Factors',
    factors: [
      { name: 'asset.business_criticality', displayName: 'Business Criticality' },
      { name: 'asset.has_pii', displayName: 'Asset has PII' },
    ],
    configTypes: GARTNER_MOCK_BASE_SCORES[1].baseScore.config.configTypes,
  },
];

// ---------------------------------------------------------------------------
// Entity weights (weight contribution panel)
// ---------------------------------------------------------------------------

export const GARTNER_MOCK_ENTITY_WEIGHTS: EntityWeight[] = [
  { projectionId: 'uber_assets', name: 'Asset Business Context', weight: 30, factorCount: 0, allowedAggTypes: [], hasAggregation: false },
  { projectionId: 'uber_findings', name: 'Finding', weight: 30, factorCount: 0, allowedAggTypes: [], hasAggregation: false },
  {
    projectionId: 'uber_policy_violations',
    name: 'Policy Violation Severity',
    weight: 20,
    factorCount: 0,
    allowedAggTypes: [],
    hasAggregation: false,
  },
  { projectionId: 'uber_alerts', name: 'Alert', weight: 20, factorCount: 0, allowedAggTypes: [], hasAggregation: false },
  {
    projectionId: 'uber_identity_findings',
    name: 'Identity Finding',
    weight: 0,
    factorCount: 0,
    allowedAggTypes: [],
    hasAggregation: false,
  },
  { projectionId: 'uber_users', name: 'User', weight: 0, factorCount: 0, allowedAggTypes: [], hasAggregation: false },
  { projectionId: 'uber_tenants', name: 'Tenant', weight: 0, factorCount: 0, allowedAggTypes: [], hasAggregation: false },
];

// ---------------------------------------------------------------------------
// Asset score explanation mock — shown in the Score Explanation tab on assets
// when GartnerDemo FF is on.
// ---------------------------------------------------------------------------

// Weights: assets=30%, findings=30%, policy_violations=20%, alerts=20%
// Assets:  criticality=5→30, has_pii=true→15 → base=45, contributed=45×0.30=13.5≈14
// Findings (CVE type only — configTypes are mutually exclusive per finding.type):
//   Exploitability (60%): vuln_in_wild=15, exploit_on_decoy=20, active_exploitation=20, rest=0 → raw=55, weighted=55×0.6=33
//   Exposure (40%):       mitigated_by_zpa=0, mitigated_by_zia=0 → raw=0, weighted=0
//   base=33, uncapped=33, contributed=33×0.30≈10
// Policy:  base=60, contributed=60×0.20=12
// Alerts:  base=30, contributed=30×0.20=6
// Final:   14+10+12+6=42
export const GARTNER_MOCK_ASSET_SCORE_EXPLANATION: ScoreExplanationData = {
  scoreSummary: { totalScore: '42', uncappedScore: '42' },
  entityContributions: [
    {
      projectionName: 'uber_assets',
      weight: 0.3,
      aggType: '',
      contributedScore: '14',
      baseScoreExplanation: {
        scoreSummary: { totalScore: '45', uncappedScore: '45' },
        factorGroups: [
          {
            groupName: 'DEFAULT',
            weight: 1,
            totalScore: '45',
            uncappedScore: '45',
            factors: [
              { factorField: 'asset.business_criticality', factorScore: '30', factorValue: '5' },
              { factorField: 'asset.has_pii', factorScore: '15', factorValue: 'true' },
            ],
          },
        ],
      },
    },
    {
      // CVE finding — Exploitability + Exposure groups (Misconfig is a separate configType, never mixed)
      projectionName: 'uber_findings',
      weight: 0.3,
      aggType: 'MAX',
      contributedScore: '10',
      baseScoreExplanation: {
        scoreSummary: { totalScore: '33', uncappedScore: '33' },
        factorGroups: [
          {
            groupName: 'Exploitability',
            weight: 0.6,
            totalScore: '33',
            uncappedScore: '55',
            factors: [
              { factorField: 'finding.vulnerability_in_wild', factorScore: '15', factorValue: 'true' },
              { factorField: 'finding.exploit_on_decoy', factorScore: '20', factorValue: 'true' },
              { factorField: 'finding.involved_in_ransomware', factorScore: '0', factorValue: 'false' },
              { factorField: 'finding.active_exploitation', factorScore: '20', factorValue: 'true' },
              { factorField: 'finding.exploit_maturity_score', factorScore: '0', factorValue: '0' },
            ],
          },
          {
            groupName: 'Exposure',
            weight: 0.4,
            totalScore: '0',
            uncappedScore: '0',
            factors: [
              { factorField: 'finding.mitigated_by_zpa', factorScore: '0', factorValue: 'false' },
              { factorField: 'finding.mitigated_by_zia', factorScore: '0', factorValue: 'false' },
            ],
          },
        ],
      },
    },
    {
      projectionName: 'uber_policy_violations',
      weight: 0.2,
      aggType: '',
      contributedScore: '12',
      fieldScoreDetails: [{ fieldValue: 'Critical Policy', score: 60 }],
      baseScoreExplanation: {
        scoreSummary: { totalScore: '60', uncappedScore: '60' },
      },
    },
    {
      projectionName: 'uber_alerts',
      weight: 0.2,
      aggType: 'MAX',
      contributedScore: '6',
      baseScoreExplanation: {
        scoreSummary: { totalScore: '30', uncappedScore: '30' },
        scoreDistribution: [
          { count: '3', name: 'Critical' },
          { count: '5', name: 'High' },
          { count: '8', name: 'Medium' },
        ],
      },
    },
  ],
};
